import { useState } from "react";

// Chase attributes (Room Boundary Intelligence: Chase as a first-class
// object, per the vision doc's Section 12) for one cavity Edge Run — a real
// gap between two rooms already detected/classified by detectEdges(); this
// flyout is where the estimator adds detail beyond the bare "cavity"
// classification (type, clear width, accessibility, notes), independent of
// sp_SaveTakeoffEdgeOverride's shared/cavity/ignore toggle. Same per-field
// autosave convention as RoomDetailFlyout/ConditionDetailFlyout — no
// separate Save button.
const fld = { fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 3 };
const inp = { height: 32, width: "100%", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "0 9px", fontSize: 13, color: "var(--text)", background: "var(--surface)", outline: "none", boxSizing: "border-box" };

// Open vocabulary per the vision doc's own admonition against hardcoding —
// this is a starting set, not a closed enum; "Other" plus Notes covers
// anything not listed yet.
const CHASE_TYPES = [
  { value: "mechanical", label: "Mechanical" },
  { value: "electrical", label: "Electrical" },
  { value: "process",    label: "Process utility" },
  { value: "return_air", label: "Return air" },
  { value: "other",      label: "Other" },
];

export default function ChaseDetailFlyout({
  edge,      // the cavity Edge Run being annotated (has .a, .b, .runKey, .gapFt, .overlapFt)
  attrs,     // current { chaseType, clearWidthFt, isAccessible, notes } for this run, or {}
  isLocked,
  onSave,    // (attrs) -> persists (sp_SaveChaseAttributes)
  onClose,
}) {
  const [draft, setDraft] = useState(() => ({
    chaseType: attrs?.chaseType || "",
    clearWidthFt: attrs?.clearWidthFt ?? "",
    isAccessible: !!attrs?.isAccessible,
    notes: attrs?.notes || "",
  }));

  function set(patch) {
    const next = { ...draft, ...patch };
    setDraft(next);
    onSave(next);
  }

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)", zIndex: 299 }} />
      <div style={{
        position: "fixed", top: 0, right: 0, width: 380, height: "100vh",
        background: "var(--surface)", borderLeft: "1px solid var(--cs-border)",
        boxShadow: "-4px 0 32px rgba(0,0,0,0.12)", zIndex: 300,
        display: "flex", flexDirection: "column",
      }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--cs-border)", flexShrink: 0, background: "#0c4a6e", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>🕳️ Chase Details</div>
            <div style={{ fontSize: 12, color: "#93c5fd", marginTop: 3 }}>
              {edge.a.RoomName} ↔ {edge.b.RoomName} · {edge.gapFt?.toFixed(1)}′ gap detected
            </div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.1)", border: "none", cursor: "pointer", fontSize: 18, color: "#fff", lineHeight: 1, padding: "4px 8px", borderRadius: 6 }}>✕</button>
        </div>

        <div style={{ flex: 1, overflowY: "auto" }}>
          <div style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <label style={fld}>Chase Type</label>
              <select
                value={draft.chaseType}
                disabled={isLocked}
                onChange={e => set({ chaseType: e.target.value })}
                style={inp}
              >
                <option value="">— not set —</option>
                {CHASE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
              <div style={{ flex: 1 }}>
                <label style={fld}>Clear Width (ft)</label>
                <input
                  type="number" min={0} step="any"
                  value={draft.clearWidthFt}
                  disabled={isLocked}
                  placeholder={`— detected ${edge.gapFt?.toFixed(1)}′`}
                  style={{ ...inp, textAlign: "right" }}
                  onChange={e => setDraft(d => ({ ...d, clearWidthFt: e.target.value }))}
                  onBlur={e => set({ clearWidthFt: e.target.value === "" ? "" : +e.target.value })}
                />
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 6, height: 32, fontSize: 12.5, color: "var(--text2)", cursor: isLocked ? "default" : "pointer" }}>
                <input
                  type="checkbox"
                  checked={draft.isAccessible}
                  disabled={isLocked}
                  onChange={e => set({ isAccessible: e.target.checked })}
                />
                Accessible
              </label>
            </div>
            <div>
              <label style={fld}>Notes</label>
              <input
                value={draft.notes}
                disabled={isLocked}
                placeholder="Framing / closure panels / access doors, etc."
                style={inp}
                onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))}
                onBlur={e => set({ notes: e.target.value.trim() })}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
